import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartOptions, ChartData } from 'chart.js';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-doughnut-chart',
  templateUrl: './doughnut-chart.component.html',
  styleUrls: ['./doughnut-chart.component.css']
})
export class DoughnutChartComponent implements OnChanges, OnInit {
  constructor(private translate: TranslateService) {}
  /**
   * Title shown above chart
   */
  @Input() title: string = '';

  /**
   * Labels for each segment, e.g. ['Cash bills', 'Postpaid bills', ...]
   */
  @Input() labels: string[] = [];

  /**
   * Data values for each segment: could be counts or percentages.
   * If using percentages, ensure they sum to ~100.
   */
  @Input() data: number[] = [];

  /**
   * Total number to show in center. For example totalInvoices or totalValue, etc.
   * You can set a string too, or number.
   */
  @Input() total: string | number = '';

  /**
   * Optional subtext under center text, e.g. 'Invoices' or currency etc.
   */
  @Input() centerSubText?: string;

  /**
   * Optional array of colors (strings, e.g. '#4dc9f6', 'rgba(75,192,192,0.6)')
   * If not provided, default colors will be used.
   */
  @Input() colors?: string[];

  // Internals for Chart.js ng2-charts
  public chartLabels: string[] = [];
  public chartData: ChartData<'doughnut', number[], string> = {
    datasets: [
      { data: [], backgroundColor: [] }
    ]
  };
  public chartOptions: ChartOptions<'doughnut'> = {};

  // Legend items for manual legend below chart
  public legendItems: Array<{ label: string; color: string; displayValue: string }> = [];

  ngOnChanges(changes: SimpleChanges): void {
    // Whenever inputs change, rebuild chart config
    this.buildChart();
  }

  ngOnInit() {
    this.buildChart();
  }

  private buildChart() {
    // Prepare labels
    this.chartLabels = (this.labels || []).map(label => this.translate.instant(label));

    // Prepare data dataset by updating the first dataset's data property


    // Colors: if provided, use them; else fallback to a default palette
    let bgColors: string[] = [];
    if (this.colors && this.colors.length >= this.data.length) {
      bgColors = this.colors.slice(0, this.data.length);
    } else {
      // default fallback palette (as many as needed)
      const defaultPalette = [
        '#36A2EB', // blue
        '#FF6384', // red
        '#FFCE56', // yellow
        '#4BC0C0', // green
        '#9966FF', // purple
        '#FF9F40', // orange
        // add more if you expect more segments
      ];
      // Cycle or slice
      for (let i = 0; i < this.data.length; i++) {
        bgColors.push(defaultPalette[i % defaultPalette.length]);
      }
    }

    const isAllZero = !this.data || this.data.length === 0 || this.data.every(v => !v);

    let finalData = this.data || [];
    let finalBgColors = bgColors;

    if (isAllZero) {
      finalData = [1]; // Render a full circle
      finalBgColors = ['#e9ecef']; // Grey placeholder color
    }

    // Build single dataset
    const dataset = {
      data: finalData,
      backgroundColor: finalBgColors,
    };

    // Assign chartData freshly so Chart.js/ng2-charts sees a clean new object
    this.chartData = {
      labels: isAllZero ? ['No Data'] : this.chartLabels,
      datasets: [dataset]
    };

    // Chart options
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false // we use manual legend
        },
        tooltip: {
          callbacks: {
            // Show label and value or percentage as desired:
            label: (context) => {
              if (isAllZero) return '0';
              const label = context.label || '';
              const value = context.raw;
              return `${label}: ${value}${this.isPercentageData() ? '%' : ''}`;
            }
          }
        }
      },
      cutout: '70%', // adjust doughnut thickness; e.g. '70%' or number of px
    };

    // Build legend items: label + color + displayValue (e.g. “55%” or count)
    this.legendItems = this.labels.map((lbl, idx) => {
      const val = this.data[idx];
      let displayValue = '';
      if (typeof val === 'number') {
        // If data sum to 100-ish, treat as percentage; otherwise you might want both count and percentage.
        // Here we assume data is percentage if total sum ~100, or if user passed percentages explicitly.
        // You can customize: e.g. pass an extra Input telling chart if data are percentages or counts.
        displayValue = `${val}${this.isPercentageData() ? '%' : ''}`;
      } else {
        displayValue = String(val);
      }
      return {
        label: lbl,
        color: bgColors[idx],
        displayValue
      };
    });

    // Center text
    this.centerText = this.total;
  }

  // A simple heuristic: if data sum is between 95 and 105, treat as percentage dataset
  private isPercentageData(): boolean {
    if (!this.data || this.data.length === 0) {
      return false;
    }
    const sum = this.data.reduce((acc, v) => acc + (typeof v === 'number' ? v : 0), 0);
    return sum > 95 && sum < 105;
  }

  // centerText bound in template
  public centerText: string | number = '';
}
